import { useEffect, useRef, type RefObject } from 'react';

const SELECTEUR_FOCUS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

interface DialogueActif {
  id: symbol;
  priorite: number;
  ordre: number;
  panneau: HTMLElement;
  origine: HTMLElement | null;
  fermer: () => void;
}
interface Isolement { element: HTMLElement; inert: boolean; ariaHidden: string | null }
const dialogues: DialogueActif[] = [];
let prochainOrdre = 0;
let isolements: Isolement[] = [];

function sommet(): DialogueActif | undefined {
  return [...dialogues].sort((a, b) => a.priorite - b.priorite || a.ordre - b.ordre).at(-1);
}

function estVisible(element: HTMLElement): boolean {
  return element.getClientRects().length > 0 && element.getAttribute('aria-hidden') !== 'true';
}

function focusables(racine: HTMLElement): HTMLElement[] {
  return Array.from(racine.querySelectorAll<HTMLElement>(SELECTEUR_FOCUS)).filter(estVisible);
}

function rendreIsolements(): void {
  for (const ancien of isolements) {
    ancien.element.inert = ancien.inert;
    if (ancien.ariaHidden === null) ancien.element.removeAttribute('aria-hidden');
    else ancien.element.setAttribute('aria-hidden', ancien.ariaHidden);
  }
  isolements = [];
  /* Le parent du panneau est l'overlay complet. Son voile reste cliquable ;
     l'isolement commence aux surfaces situees derriere cet overlay. */
  const panneau = sommet()?.panneau ?? null;
  let enfant: HTMLElement | null = panneau?.parentElement ?? panneau;
  while (enfant !== null && enfant.parentElement !== null) {
    const parent: HTMLElement = enfant.parentElement;
    for (const voisin of Array.from(parent.children)) {
      if (!(voisin instanceof HTMLElement) || voisin === enfant) continue;
      isolements.push({ element: voisin, inert: voisin.inert, ariaHidden: voisin.getAttribute('aria-hidden') });
      voisin.inert = true;
      voisin.setAttribute('aria-hidden', 'true');
    }
    enfant = parent === document.body ? null : parent;
  }
}

/** Ce qui garde sa touche Echap dans une fenetre : la liste de choix ouverte. */
export const ATTRIBUT_GARDE_ECHAP = '[data-garde-echap]';

/**
 * Garde le clavier dans la fenetre active et rend le focus a son declencheur.
 * La pile est partagee par Modal et les overlays du shell : seul le dernier
 * ouvert traite Tab et Escape, y compris quand le lanceur rapide les recouvre.
 */
export function useDialogFocus(
  actif: boolean,
  panneau: RefObject<HTMLElement | null>,
  fermer: () => void,
  priorite = 0,
): void {
  const fermerRef = useRef(fermer);
  useEffect(() => {
    fermerRef.current = fermer;
  }, [fermer]);
  useEffect(() => {
    const racine = panneau.current;
    if (!actif || racine === null) return;
    return activerDialogue(racine, () => fermerRef.current(), priorite);
  }, [actif, panneau, priorite]);
}

/** Moteur DOM testable sans React. Le callback est lu au moment du geste. */
export function activerDialogue(
  racine: HTMLElement,
  fermer: () => void,
  priorite = 0,
): () => void {
    const dialogue: DialogueActif = {
      id: Symbol('dialogue'), priorite, ordre: prochainOrdre++, panneau: racine,
      origine: document.activeElement instanceof HTMLElement ? document.activeElement : null,
      fermer,
    };
    dialogues.push(dialogue);
    rendreIsolements();
    if (sommet()?.id === dialogue.id) (focusables(racine)[0] ?? racine).focus();

    const auClavier = (event: KeyboardEvent): void => {
      if (sommet()?.id !== dialogue.id) return;
      if (event.key === 'Escape') {
        /* UNE LISTE OUVERTE DANS LA FENETRE (`data-garde-echap`, l'OptionList)
           ferme d'abord elle-meme : sans ce passage, Echap dans le choix d'une
           tache refermait tout « Nouveau créneau » et perdait la saisie
           (recette 3 du lot du 14/10/2026). */
        const cible = event.target as { closest?: (selecteur: string) => unknown } | null;
        if (typeof cible?.closest === 'function' && cible.closest(ATTRIBUT_GARDE_ECHAP) != null) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        dialogue.fermer();
        return;
      }
      if (event.key !== 'Tab') return;
      const elements = focusables(racine);
      if (elements.length === 0) {
        event.preventDefault();
        racine.focus();
        return;
      }
      const premierElement = elements[0];
      const dernierElement = elements[elements.length - 1];
      const actifMaintenant = document.activeElement;
      const dehors = !racine.contains(actifMaintenant);
      if (event.shiftKey && (actifMaintenant === premierElement || dehors)) {
        event.preventDefault();
        dernierElement?.focus();
      } else if (!event.shiftKey && (actifMaintenant === dernierElement || dehors)) {
        event.preventDefault();
        premierElement?.focus();
      }
    };
    document.addEventListener('keydown', auClavier, true);
    return () => {
      document.removeEventListener('keydown', auClavier, true);
      const etaitSommet = sommet()?.id === dialogue.id;
      const rang = dialogues.findIndex((item) => item.id === dialogue.id);
      if (rang >= 0) dialogues.splice(rang, 1);
      rendreIsolements();
      if (!etaitSommet) return;
      const suivant = sommet();
      if (suivant !== undefined) {
        const origineToujoursDansLeSuivant =
          dialogue.origine?.isConnected === true && suivant.panneau.contains(dialogue.origine);
        if (origineToujoursDansLeSuivant) dialogue.origine?.focus();
        else (focusables(suivant.panneau)[0] ?? suivant.panneau).focus();
      }
      else if (dialogue.origine?.isConnected) dialogue.origine.focus();
    };
}
